/**
 * Latency / failure simulation for the mock API (plan §4.4).
 *
 * - Normal: uniform 50–250ms per response, with a 6% chance of a slow
 *   500–800ms response instead.
 * - Slow (dev/test override): every response takes 500–800ms.
 * - None: no artificial delay at all.
 * - Mutations additionally fail with a simulated error at `failMutations`
 *   rate (default 1%), independent of the latency setting — GETs never
 *   fail. Playwright can force `{ latency: "none", failMutations: 1 }` for
 *   fast, deterministic rollback tests.
 */

export type LatencyMode = "normal" | "slow" | "none";

export interface MockSettings {
  latency: LatencyMode;
  failMutations: number;
}

const STORAGE_KEY = "studside:mock";
const SLOW_MIN_MS = 500;
const SLOW_MAX_MS = 800;
const NORMAL_MIN_MS = 50;
const NORMAL_MAX_MS = 250;
const SLOW_TAIL_CHANCE = 0.06;

const DEFAULTS: MockSettings = { latency: "normal", failMutations: 0.01 };

export function getSettings(): MockSettings {
  if (typeof localStorage === "undefined") return DEFAULTS;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULTS;
    const parsed = JSON.parse(raw) as Partial<MockSettings>;
    return { ...DEFAULTS, ...parsed };
  } catch {
    return DEFAULTS;
  }
}

export function setSettings(partial: Partial<MockSettings>): MockSettings {
  const next = { ...getSettings(), ...partial };
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    // Private browsing / quota exceeded — the panel still reflects state in
    // memory for this page load, it just won't persist across reloads.
  }
  if (typeof document !== "undefined") {
    document.dispatchEvent(new CustomEvent<MockSettings>("studside:mock-settings", { detail: next }));
  }
  return next;
}

function uniform(min: number, max: number): number {
  return min + Math.random() * (max - min);
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export class MockMutationError extends Error {
  constructor() {
    super("Simulated mutation failure");
    this.name = "MockMutationError";
  }
}

export async function delayLatency(): Promise<void> {
  const settings = getSettings();

  if (settings.latency !== "none") {
    const slowTail = settings.latency === "slow" || Math.random() < SLOW_TAIL_CHANCE;
    const ms = slowTail ? uniform(SLOW_MIN_MS, SLOW_MAX_MS) : uniform(NORMAL_MIN_MS, NORMAL_MAX_MS);
    await sleep(ms);
  }
}

export async function delay(mutation = false): Promise<void> {
  await delayLatency();
  if (mutation && Math.random() < getSettings().failMutations) {
    throw new MockMutationError();
  }
}

type MockWindow = Window & { __studsideMock?: { set: typeof setSettings; get: typeof getSettings } };

function renderPanel(container: HTMLElement): void {
  const current = getSettings();
  container.innerHTML = "";
  container.style.cssText = [
    "position:fixed",
    "bottom:16px",
    "right:16px",
    "z-index:2147483647",
    "background:#111114",
    "color:#f4f4f5",
    "border:1px solid #2a2a30",
    "border-radius:8px",
    "padding:12px 14px",
    "font:12px/1.5 ui-monospace,'JetBrains Mono',monospace",
    "min-width:220px",
    "box-shadow:0 8px 24px rgba(0,0,0,0.4)",
  ].join(";");

  const title = document.createElement("div");
  title.textContent = "Mock controls (Shift+M)";
  title.style.cssText = "font-weight:600;margin-bottom:8px;";
  container.append(title);

  const latencyRow = document.createElement("div");
  latencyRow.style.cssText = "display:flex;gap:6px;margin-bottom:8px;";
  (["normal", "slow", "none"] as const).forEach((mode) => {
    const button = document.createElement("button");
    button.type = "button";
    button.textContent = mode;
    const active = current.latency === mode;
    button.style.cssText = [
      "flex:1",
      "padding:4px 6px",
      "border-radius:4px",
      "cursor:pointer",
      `background:${active ? "#2563EB" : "#1c1c22"}`,
      `border:1px solid ${active ? "#2563EB" : "#2a2a30"}`,
      "color:#f4f4f5",
    ].join(";");
    button.addEventListener("click", () => {
      setSettings({ latency: mode });
      renderPanel(container);
    });
    latencyRow.append(button);
  });
  container.append(latencyRow);

  const failRow = document.createElement("label");
  failRow.style.cssText = "display:flex;align-items:center;gap:6px;";
  const failLabel = document.createElement("span");
  failLabel.textContent = `fail rate: ${Math.round(current.failMutations * 100)}%`;
  failLabel.style.cssText = "flex:1;";
  const failInput = document.createElement("input");
  failInput.type = "range";
  failInput.min = "0";
  failInput.max = "100";
  failInput.step = "1";
  failInput.value = String(Math.round(current.failMutations * 100));
  failInput.addEventListener("input", () => {
    const failMutations = Number(failInput.value) / 100;
    failLabel.textContent = `fail rate: ${failInput.value}%`;
    setSettings({ failMutations });
  });
  failRow.append(failLabel, failInput);
  container.append(failRow);
}

function togglePanel(): void {
  const existingId = "studside-mock-panel";
  const existing = document.getElementById(existingId);
  if (existing) {
    existing.remove();
    return;
  }
  const panel = document.createElement("div");
  panel.id = existingId;
  document.body.append(panel);
  renderPanel(panel);
}

/** Installs the `window.__studsideMock` control surface and the Shift+M dev panel. */
export function installMockControls(): void {
  if (typeof window === "undefined") return;
  (window as MockWindow).__studsideMock = { set: setSettings, get: getSettings };
  window.addEventListener("keydown", (event) => {
    if (event.shiftKey && event.key.toLowerCase() === "m") togglePanel();
  });
}
